import {
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { useConfirm } from 'components/ConfirmDialog';
import {
  SaveAlertData,
  useWeatherAlert,
} from 'features/weatherAlerts/hooks/useWeatherAlert';
import { WeatherAlert } from 'features/weatherAlerts/weatherAlerts.types';
import { useWeatherStationPrecipitationEnabled } from 'features/weatherStations/hooks/useWeatherStationPrecipitationEnabled';
import { WeatherStation } from 'features/weatherStations/weatherStations.types';
import { useFormBlocker } from 'hooks/useFormBlocker';
import { logUnexpectedErrorAndNotifyUserWithTryAgain } from 'shared/handleUnexpectedError';
import { switchFormRowLabelSx } from 'theme/switchFormRowLabelSx';
import * as Utils from './WeatherStationAlertForm.utils';

type WeatherStationAlertFormProps = {
  weatherStation: WeatherStation;
  alert?: WeatherAlert;
  fromRoute: string;
};

type FormValues = {
  fieldName?: Utils.FieldName;
  isEnabled?: boolean;
  operator?: Utils.Operator;
  thresholdValue?: number | string;
};

export const WeatherStationAlertForm = (
  props: WeatherStationAlertFormProps,
): JSX.Element => {
  const { weatherStation, alert, fromRoute } = props;
  const { t } = useTranslation();
  const { saveAlert, deleteAlert } = useWeatherAlert({ alertId: alert?.id });
  const isPrecipitationEnabled = useWeatherStationPrecipitationEnabled();
  const confirm = useConfirm();

  const { control, formState, handleSubmit } = useForm<FormValues>({
    resolver: yupResolver(Utils.getValidationSchema(t)),
    values: {
      fieldName:
        alert?.criteria[0].fieldName ?? Utils.ATMOSPHERIC_FIELD_NAMES[0],
      isEnabled: alert?.isEnabled ?? true,
      operator: alert?.criteria[0].operator ?? Utils.OPERATORS[0],
      thresholdValue: Utils.convertExistingThresholdValue(
        alert?.criteria[0].thresholdValue,
        alert?.criteria[0].fieldName ?? Utils.ATMOSPHERIC_FIELD_NAMES[0],
      ),
    },
  });

  const { unblockedNavigate } = useFormBlocker({
    shouldBlockNavigation: Boolean(formState.isDirty),
  });

  // Needed so we can determine the adornment
  const selectedFieldName = useWatch({ control, name: 'fieldName' });

  const navigateBack = (): void => unblockedNavigate(fromRoute);

  const onDelete = async (): Promise<void> => {
    if (!alert) return;

    const resp = await confirm(t('weatherStations.alerts.deleteConfirmation'), {
      title: t('weatherStations.alerts.deleteNotification'),
    });
    if (!resp) return;

    try {
      await deleteAlert();
      navigateBack();
    } catch (error) {
      logUnexpectedErrorAndNotifyUserWithTryAgain(t, { error });
    }
  };

  const onSave: SubmitHandler<FormValues> = async (values): Promise<void> => {
    const thresholdValue = Utils.getThresholdValue(
      values.thresholdValue,
      values.fieldName ?? '',
    );
    const data = {
      id: alert?.id,
      ...(alert?.id ? { isEnabled: values.isEnabled } : {}), // Only set isEnabled when editing
      stationId: weatherStation.id,
      criteria: [
        {
          fieldName: values.fieldName,
          operator: values.operator,
          thresholdValue,
        },
      ],
    } as SaveAlertData;

    try {
      await saveAlert(data);
      navigateBack();
    } catch (finalError) {
      logUnexpectedErrorAndNotifyUserWithTryAgain(t, { error: finalError });
    }
  };

  const fieldNameMenuItems = Utils.getFieldNameMenuItems(
    t,
    isPrecipitationEnabled,
  );
  const operatorMenuItems = Utils.getOperatorMenuItems(t);

  return (
    <Stack spacing={2}>
      <Typography variant="h2">{weatherStation?.name}</Typography>

      <Typography>{t('weatherStations.alerts.heading')}</Typography>

      <Controller
        control={control}
        name="fieldName"
        render={({ field, fieldState }) => {
          const { ref, value, ...muiFieldProps } = field;
          return (
            <FormControl required>
              <InputLabel id="fieldName-input-label" required>
                {t('weatherStations.alerts.inputs.fieldName.label')}
              </InputLabel>
              <Select
                label={t('weatherStations.alerts.inputs.fieldName.label')}
                labelId="fieldName-input-label"
                error={Boolean(fieldState.error)}
                fullWidth
                value={value}
                {...muiFieldProps}
              >
                {Object.keys(fieldNameMenuItems).map((key) => (
                  <MenuItem key={key} value={key}>
                    {fieldNameMenuItems[key]}
                  </MenuItem>
                ))}
              </Select>
              {fieldState.error?.message && (
                <FormHelperText error>
                  {fieldState.error.message}
                </FormHelperText>
              )}
            </FormControl>
          );
        }}
      />

      <Controller
        control={control}
        name="operator"
        render={({ field, fieldState }) => {
          const { ref, value, ...muiFieldProps } = field;
          return (
            <FormControl required>
              <InputLabel id="operator-input-label" required>
                {t('weatherStations.alerts.inputs.operator.label')}
              </InputLabel>
              <Select
                label={t('weatherStations.alerts.inputs.operator.label')}
                labelId="operator-input-label"
                error={Boolean(fieldState.error)}
                fullWidth
                value={value}
                {...muiFieldProps}
              >
                {Object.keys(operatorMenuItems).map((key) => (
                  <MenuItem key={key} value={key}>
                    {operatorMenuItems[key]}
                  </MenuItem>
                ))}
              </Select>
              {fieldState.error?.message && (
                <FormHelperText error>
                  {fieldState.error.message}
                </FormHelperText>
              )}
            </FormControl>
          );
        }}
      />

      <Controller
        control={control}
        name="thresholdValue"
        render={({ field, fieldState }) => {
          const { ref, value, ...muiFieldProps } = field;
          return (
            <FormControl required>
              <TextField
                label={t('weatherStations.alerts.inputs.thresholdValue.label')}
                error={Boolean(fieldState.error)}
                fullWidth
                helperText={
                  fieldState.error?.message ||
                  t('weatherStations.alerts.inputs.thresholdValue.helperText')
                }
                required
                slotProps={{
                  htmlInput: {
                    inputMode: 'decimal',
                    maxLength: 6,
                  },
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        {
                          Utils.mapFieldNameToAdornment[
                            selectedFieldName ??
                              Utils.ATMOSPHERIC_FIELD_NAMES[0]
                          ]
                        }
                      </InputAdornment>
                    ),
                  },
                }}
                value={value}
                {...muiFieldProps}
              />
            </FormControl>
          );
        }}
      />

      {alert && (
        <Controller
          control={control}
          name="isEnabled"
          render={({ field }) => {
            const { ref, value, ...muiFieldProps } = field;
            return (
              <FormControl fullWidth>
                <FormControlLabel
                  control={
                    <Switch checked={value} inputRef={ref} {...muiFieldProps} />
                  }
                  label={t('weatherStations.alerts.inputs.isEnabled.label')}
                  labelPlacement="start"
                  sx={switchFormRowLabelSx}
                />
              </FormControl>
            );
          }}
        />
      )}

      {alert && (
        <Button
          fullWidth
          size="large"
          disabled={formState.isSubmitting}
          onClick={onDelete}
          color="error"
        >
          {t('weatherStations.alerts.deleteNotification')}
        </Button>
      )}

      <Button
        fullWidth
        size="large"
        disabled={formState.isSubmitting}
        onClick={handleSubmit(onSave)}
      >
        {alert
          ? t('weatherStations.alerts.saveNotification')
          : t('weatherStations.alerts.addNotification')}
      </Button>
    </Stack>
  );
};
